/**
 * The demo world: 28 hand-specified tokens, their tapes, their holders, a
 * connected-user persona and the protocol aggregates over all of it.
 *
 * Design rules that shaped this file:
 *
 *  - **Nothing is invented twice.** Every displayed number is derived from the
 *    curve state via `curve.ts`, or summed over the tokens. If the explore card
 *    and the token page disagree, the demo has failed at its only job.
 *  - **Editorial, not procedural.** Names, symbols, glyphs, blurbs, phase, scale,
 *    24h change and target floor ratio are written by hand per token; only the
 *    texture (timings, trade sizes, holder tails, chart noise) is generated. A
 *    procedural namer produces "Token 3" and the UI immediately looks fake.
 *  - **The floor never falls.** Every path through this file — generation, trade
 *    application, jitter — either raises `floorPrice` or leaves it alone.
 *
 * ## Sizing the curve so a deep floor is actually solvent
 *
 * `floorRatio = floorPrice / spot = floorReserve / (spot * redeemableSupply)`, and
 * for a constant-product curve `spot * redeemableSupply = raised * m` where
 * `m = 1 + raised / vQuote0`. So a token with a 0.78 floor ratio needs
 * `floorReserve = 0.78 * m * raised`, and the reserve can only ever be funded out
 * of fees on cumulative buy volume — it cannot exceed what came in.
 *
 * We therefore pick `m` per token as `min(desired, 0.86 / floorRatio)`, i.e. we
 * FLATTEN the curve (larger `vQuote0`) for deep-floor tokens instead of inventing
 * a reserve bigger than the raise. That is also the economically honest reading:
 * "almost all guarantee, barely any story" is a flat curve with a fat virtual
 * reserve. The implied cumulative buy volume needed to fund each reserve is
 * computed explicitly (`impliedBuyVolume`) and used as the token's lifetime
 * volume floor, so the volume figures support the floor figures.
 */

import { toWei, weiToNumber } from '@/lib/format';
import {
  BLOCK_MS,
  buyExactIn,
  curveStateFromToken,
  effectiveFeeBPS,
  floorPrice as floorPriceOf,
  graduationProgress,
  spotPrice,
  type CurveState,
} from './curve';
import { makeRng, type Rng } from './rng';
import { anchorPriceAt, buildFloorLockedSeries, priceAnchors } from './series';
import type {
  ActivityKind,
  DemoActivity,
  DemoHolder,
  DemoPosition,
  DemoToken,
  DemoTrade,
  DemoUser,
  DemoWorld,
  Hex,
  HolderLabel,
  ProtocolStats,
  TokenLinks,
  TokenPhase,
  TradeSide,
} from './types';

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;

/** Curve supply cap for every token — one billion, the launchpad convention. */
const SUPPLY_CAP = 1_000_000_000;

/** Input accepted by `/launch`, mirrored by `MarketApi.launchToken`. */
export interface LaunchInput {
  name: string;
  symbol: string;
  description: string;
  glyph: string;
  tags: string[];
  links?: TokenLinks;
  devBuyEth: number;
  feeBPS: number;
  floorShareBPS: number;
  buybackFeeBPS: number;
  antiSnipeStartBPS: number;
  antiSnipeWindow: number;
  graduationQuoteThresholdEth: number;
  /**
   * Required by the factory whenever `buybackFeeBPS > 0`: a non-zero buyback
   * slice with a zero threshold accrues treasury ETH that `executeBuyback` can
   * never spend and nobody can withdraw, so the factory rejects that pair.
   */
  buybackThresholdEth: number;
  /** 0–360. The wizard's mark colour; falls back to golden-angle spacing. */
  hue?: number;
}

/**
 * Exact wei from an ETH/token float.
 *
 * `toWei` takes a decimal string, and `String(4.2e-9)` is exponential notation
 * that `parseEther` rejects — so fix the point first. Prices here are ~1e-9 ETH
 * and supplies ~1e9 tokens, both comfortably inside `toFixed`'s range.
 */
function wei(n: number): bigint {
  if (!Number.isFinite(n) || n <= 0) return 0n;
  return toWei(n.toFixed(18));
}

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

/* ────────────────────────────────────────────────────────────────────────────
   Editorial token table
   ──────────────────────────────────────────────────────────────────────────── */

interface TokenSpec {
  name: string;
  symbol: string;
  description: string;
  glyph: string;
  tags: string[];
  phase: TokenPhase;
  /** Real ETH the curve has taken in. */
  raised: number;
  /** Fraction of the graduation threshold reached; sets the threshold. */
  progress: number;
  holders: number;
  change24h: number;
  /** Target `floorPrice / spotPrice`. */
  floorRatio: number;
}

/** Tag vocabulary the filter chips are built from. */
export const TAGS = ['meme', 'ai', 'defi', 'gaming', 'infra', 'art', 'social', 'rwa'] as const;

const SPECS: TokenSpec[] = [
  // ── monsters ──────────────────────────────────────────────────────────────
  {
    name: 'Ratchet',
    symbol: 'RATCH',
    glyph: '⚙️',
    tags: ['defi', 'infra'],
    description: 'One-way gearing for the floor. Up is the only direction it knows.',
    phase: 'graduated',
    raised: 880,
    progress: 1.04,
    holders: 4210,
    change24h: 0.18,
    floorRatio: 0.72,
  },
  {
    name: 'Basilisk Compute',
    symbol: 'BSLK',
    glyph: '🧠',
    tags: ['ai', 'infra'],
    description: 'Rents you GPUs today from something that may rent you tomorrow.',
    phase: 'graduated',
    raised: 610,
    progress: 1.04,
    holders: 3104,
    change24h: -0.09,
    floorRatio: 0.61,
  },
  {
    name: 'Paperclip Maximizer',
    symbol: 'CLIP',
    glyph: '📎',
    tags: ['ai', 'meme'],
    description: 'Converts the entire float into stationery. Extremely efficient.',
    phase: 'graduating',
    raised: 470,
    progress: 0.97,
    holders: 2451,
    change24h: 1.12,
    floorRatio: 0.29,
  },
  // ── large / graduated ─────────────────────────────────────────────────────
  {
    name: 'Deep Floor Capital',
    symbol: 'DEEPFL',
    glyph: '🏛️',
    tags: ['defi', 'rwa'],
    description: 'Almost all guarantee, barely any story. On purpose.',
    phase: 'graduated',
    raised: 320,
    progress: 1.04,
    holders: 1806,
    change24h: 0.06,
    floorRatio: 0.78,
  },
  {
    name: 'Vault of Ash',
    symbol: 'ASH',
    glyph: '🔥',
    tags: ['defi'],
    description: 'Every buyback burns. The urn only gets heavier.',
    phase: 'graduated',
    raised: 210,
    progress: 1.04,
    holders: 1243,
    change24h: 0.31,
    floorRatio: 0.49,
  },
  {
    name: 'Pixel Warlord',
    symbol: 'WARLRD',
    glyph: '🗡️',
    tags: ['gaming', 'art'],
    description: 'Sixteen colours, one throne, permanent siege.',
    phase: 'graduated',
    raised: 96,
    progress: 1.04,
    holders: 782,
    change24h: -0.27,
    floorRatio: 0.44,
  },
  // ── graduating ────────────────────────────────────────────────────────────
  {
    name: 'Yield Goblin',
    symbol: 'GOBLIN',
    glyph: '🪙',
    tags: ['defi', 'meme'],
    description: 'Hoards basis points in a hole underneath the protocol.',
    phase: 'graduating',
    raised: 88,
    progress: 0.89,
    holders: 694,
    change24h: 0.42,
    floorRatio: 0.36,
  },
  {
    name: 'Latency Cartel',
    symbol: 'LATNCY',
    glyph: '⚡',
    tags: ['infra'],
    description: 'We fixed your ordering problem. You will not enjoy the invoice.',
    phase: 'graduating',
    raised: 61,
    progress: 0.94,
    holders: 523,
    change24h: -0.11,
    floorRatio: 0.33,
  },
  {
    name: 'Liquidity Gremlin',
    symbol: 'GREM',
    glyph: '👺',
    tags: ['meme', 'defi'],
    description: 'Lives in the mempool. Eats your slippage. Refuses to leave.',
    phase: 'graduating',
    raised: 41,
    progress: 0.92,
    holders: 412,
    change24h: 0.77,
    floorRatio: 0.34,
  },
  // ── trading ───────────────────────────────────────────────────────────────
  {
    name: 'Terminal Velocity',
    symbol: 'TERM',
    glyph: '📉',
    tags: ['defi'],
    description: 'Falling is fine once there is a floor to fall onto.',
    phase: 'trading',
    raised: 44,
    progress: 0.55,
    holders: 1118,
    change24h: -0.38,
    floorRatio: 0.42,
  },
  {
    name: 'Grid Oracle',
    symbol: 'GRIDO',
    glyph: '🔮',
    tags: ['ai', 'infra', 'rwa'],
    description: "Sells you tomorrow's electricity price, denominated in vibes.",
    phase: 'trading',
    raised: 31,
    progress: 0.62,
    holders: 941,
    change24h: 0.51,
    floorRatio: 0.24,
  },
  {
    name: 'Ghost Bid',
    symbol: 'GBID',
    glyph: '👻',
    tags: ['defi'],
    description: 'A bid nobody can cancel is really just a haunting.',
    phase: 'trading',
    raised: 22,
    progress: 0.51,
    holders: 608,
    change24h: 0.13,
    floorRatio: 0.38,
  },
  {
    name: 'Onchain Hamster',
    symbol: 'HAMSTR',
    glyph: '🐹',
    tags: ['meme', 'gaming'],
    description: 'A wheel, a webcam and a hot wallet. The hamster trades; you cope.',
    phase: 'trading',
    raised: 18.5,
    progress: 0.46,
    holders: 687,
    change24h: 1.66,
    floorRatio: 0.21,
  },
  {
    name: 'Feral Hog Market',
    symbol: 'HOGS',
    glyph: '🐗',
    tags: ['meme'],
    description: 'Thirty to fifty of them, all bidding at once.',
    phase: 'trading',
    raised: 12.8,
    progress: 0.43,
    holders: 476,
    change24h: 0.88,
    floorRatio: 0.17,
  },
  {
    name: 'Tarot Protocol',
    symbol: 'TAROT',
    glyph: '🃏',
    tags: ['art', 'social'],
    description: 'Draws you a card. The card is your liquidation price.',
    phase: 'trading',
    raised: 9.3,
    progress: 0.39,
    holders: 263,
    change24h: 0.44,
    floorRatio: 0.3,
  },
  {
    name: 'Moon Notary',
    symbol: 'NOTARY',
    glyph: '🌙',
    tags: ['rwa', 'infra'],
    description: 'Stamps your lunar deed. Jurisdiction pending, enthusiasm total.',
    phase: 'trading',
    raised: 7.4,
    progress: 0.37,
    holders: 204,
    change24h: 0.22,
    floorRatio: 0.26,
  },
  {
    name: 'Copium Reserve',
    symbol: 'COPE',
    glyph: '😤',
    tags: ['meme', 'social'],
    description: 'A strategic national stockpile of "we are still early".',
    phase: 'trading',
    raised: 6.2,
    progress: 0.31,
    holders: 167,
    change24h: 0.35,
    floorRatio: 0.28,
  },
  {
    name: 'Tungsten Cube Club',
    symbol: 'TUNG',
    glyph: '🧊',
    tags: ['meme', 'social'],
    description: 'Membership is heavy. Shipping is heavier.',
    phase: 'trading',
    raised: 5.6,
    progress: 0.28,
    holders: 142,
    change24h: 2.1,
    floorRatio: 0.22,
  },
  {
    name: 'Bagholder Union',
    symbol: 'UNION',
    glyph: '🧳',
    tags: ['social', 'meme'],
    description: 'Collective bargaining for people who bought the exact top.',
    phase: 'trading',
    raised: 3.1,
    progress: 0.22,
    holders: 97,
    change24h: -0.18,
    floorRatio: 0.31,
  },
  {
    name: 'Neon Abattoir',
    symbol: 'NEON',
    glyph: '🌆',
    tags: ['art'],
    description: 'Generative butchery, rendered at sixty frames a second.',
    phase: 'trading',
    raised: 1.7,
    progress: 0.12,
    holders: 59,
    change24h: -0.07,
    floorRatio: 0.2,
  },
  {
    name: 'Sisyphus Finance',
    symbol: 'SISY',
    glyph: '🪨',
    tags: ['defi'],
    description: 'Perpetual re-collateralisation. The boulder is the yield.',
    phase: 'trading',
    raised: 0.9,
    progress: 0.09,
    holders: 34,
    change24h: -0.53,
    floorRatio: 0.19,
  },
  {
    name: 'Dust Collector',
    symbol: 'DUST',
    glyph: '🧹',
    tags: ['infra'],
    description: 'Sweeps the sub-cent tails of your wallet into one sad pile.',
    phase: 'trading',
    raised: 0.55,
    progress: 0.07,
    holders: 22,
    change24h: -0.31,
    floorRatio: 0.15,
  },
  // ── launching (inside the anti-snipe window) ───────────────────────────────
  {
    name: "Grandma's Alpha",
    symbol: 'NANA',
    glyph: '👵',
    tags: ['social', 'meme'],
    description: 'She has never been wrong and she will not be explaining herself.',
    phase: 'launching',
    raised: 3.8,
    progress: 0.13,
    holders: 62,
    change24h: 0.94,
    floorRatio: 0.11,
  },
  {
    name: 'Certified Bussin',
    symbol: 'BUSSIN',
    glyph: '🚌',
    tags: ['meme'],
    description: 'Audited by three teenagers. Verdict: it is, in fact, bussin.',
    phase: 'launching',
    raised: 2.4,
    progress: 0.09,
    holders: 45,
    change24h: 5.4,
    floorRatio: 0.06,
  },
  {
    name: 'Sad Frog Index',
    symbol: 'SADFRG',
    glyph: '🐸',
    tags: ['meme', 'art'],
    description: 'Tracks the aggregate mood of every frog in the arena.',
    phase: 'launching',
    raised: 1.1,
    progress: 0.05,
    holders: 28,
    change24h: -0.21,
    floorRatio: 0.09,
  },
  {
    name: 'Schizo Signals',
    symbol: 'SCHIZO',
    glyph: '📡',
    tags: ['ai', 'social'],
    description: 'Broadcasts alpha it heard in a dream. Occasionally correct.',
    phase: 'launching',
    raised: 0.42,
    progress: 0.04,
    holders: 14,
    change24h: 2.85,
    floorRatio: 0.04,
  },
  // ── halted by the safety circuit (redeem-at-floor still open) ──────────────
  {
    name: 'Exit Liquidity',
    symbol: 'EXIT',
    glyph: '🚪',
    tags: ['meme', 'defi'],
    description: 'Honest branding. The circuit tripped; the floor did not.',
    phase: 'halted',
    raised: 27,
    progress: 0.34,
    holders: 301,
    change24h: -0.62,
    floorRatio: 0.68,
  },
  {
    name: 'Rug Museum',
    symbol: 'MUSEUM',
    glyph: '🖼️',
    tags: ['art', 'meme'],
    description: "A permanent collection of other people's exits. Now halted, fittingly.",
    phase: 'halted',
    raised: 12,
    progress: 0.18,
    holders: 181,
    change24h: -0.44,
    floorRatio: 0.57,
  },
];

/** Handles reused across traders, holders and the feed so the same wallets recur. */
const HANDLES = [
  'nullbyte', 'gwei_goblin', 'tapeworm', 'onchain_ox', 'mevmonk', 'slowrug', 'ladderfill',
  'bagpipe', 'cointoss', 'fatfinger', 'wickhunter', 'laserpig', 'sockpuppet', 'delta_neutral',
  'hodlbot', 'gasgremlin', 'pixieboots', 'saltmine', 'copetrader', 'floorwatcher', 'snipehunter',
  'bidwall', 'ashcloud', 'tarotmom', 'dexterity', 'blockparty', 'quietbid', 'thinbook',
  'rugpatrol', 'exitramp', 'tapeghost', 'lastcandle',
];

const LINK_ROOTS = ['xyz', 'fun', 'markets', 'wtf', 'lol'];

/* ────────────────────────────────────────────────────────────────────────────
   Per-token generation
   ──────────────────────────────────────────────────────────────────────────── */

interface Trader {
  address: Hex;
  handle?: string;
}

interface TokenBundle {
  token: DemoToken;
  trades: DemoTrade[];
  holders: DemoHolder[];
  /** Cumulative ETH volume over the token's whole life, all sides. */
  lifetimeVolume: number;
  /** ETH already paid out of the floor reserve to redeemers. */
  ethReturned: number;
  traders: Trader[];
}

/** Ages by phase. `launching` must stay inside its anti-snipe window. */
function ageFor(phase: TokenPhase, rng: Rng): number {
  switch (phase) {
    case 'launching':
      return rng.float(3 * MINUTE, 25 * MINUTE);
    case 'graduating':
      return rng.float(1 * DAY, 9 * DAY);
    case 'graduated':
      return rng.float(8 * DAY, 75 * DAY);
    case 'halted':
      return rng.float(3 * DAY, 20 * DAY);
    default:
      return rng.float(6 * HOUR, 40 * DAY);
  }
}

function buildTraders(rng: Rng, count: number): Trader[] {
  const handles = rng.shuffle(HANDLES);
  return Array.from({ length: count }, (_, i) => ({
    address: rng.hexAddress(),
    // Only some wallets are named; a tape of all-named addresses looks staged.
    handle: i < handles.length && rng.bool(0.45) ? handles[i] : undefined,
  }));
}

function buildToken(spec: TokenSpec, index: number, now: number): TokenBundle {
  const rng = makeRng(`bull.markets/token/${spec.symbol}`);
  const address = rng.hexAddress();
  const createdAt = Math.round(now - ageFor(spec.phase, rng));

  const graduated = spec.phase === 'graduated';
  const tradingStopped = graduated || spec.phase === 'halted';

  // ── config ────────────────────────────────────────────────────────────────
  const feeBPS = rng.pick([80, 100, 100, 120, 150]);
  // A deep floor needs a fat floor share; that is the knob a creator actually
  // turns to make one, so keep the two consistent.
  const floorShareBPS =
    spec.floorRatio >= 0.5 ? rng.pick([450, 500, 600]) : rng.pick([200, 250, 300, 350, 400]);
  const buybackFeeBPS = Math.min(feeBPS, rng.pick([20, 25, 30, 40]));
  const antiSnipeStartBPS = rng.pick([300, 450, 600, 750, 900]);
  // 900 blocks ≈ 30 min at 2s, which covers every `launching` age above.
  const antiSnipeWindow = spec.phase === 'launching' ? 900 : rng.pick([300, 600, 900, 1200, 1800]);

  // ── curve geometry ────────────────────────────────────────────────────────
  const threshold = spec.raised / spec.progress;
  // See the file header: `m` is capped so the floor reserve stays fundable.
  const m = clamp(Math.min(1 + 3.4 * spec.progress, 0.86 / spec.floorRatio), 1.04, 24);
  const vQuote0 = spec.raised / (m - 1);
  const vToken0 = SUPPLY_CAP;
  const k = vQuote0 * vToken0;
  const vQuote = vQuote0 + spec.raised;
  const vToken = k / vQuote;
  const redeemableSupply = vToken0 - vToken;
  const spot = vQuote / vToken;

  // ── floor ─────────────────────────────────────────────────────────────────
  const floorPrice = spec.floorRatio * spot;
  const baseFloor = floorPrice * redeemableSupply;
  // The anti-snipe surplus is un-absorbed over-collateralisation, so it only
  // exists pre-graduation and is largest while the window is still open.
  const snipeFrac = graduated ? 0 : spec.phase === 'launching' ? rng.float(0.25, 0.6) : rng.float(0.02, 0.15);
  const snipeSurplus = Math.min(baseFloor * snipeFrac, Math.max(0, 0.94 * spec.raised - baseFloor));
  const floorReserve = baseFloor + snipeSurplus;

  const buybackThreshold = Math.max(0.01, spec.raised * 0.005);
  const treasuryReserve = buybackThreshold * rng.float(0.05, 0.95);

  // ── volume / activity, back-solved from what the floor implies ────────────
  // ETH already redeemed out of the reserve. Cumulative floor funding is therefore
  // `floorReserve + ethReturned`, and that is what the buy volume has to explain.
  const ethReturned = floorReserve * rng.float(0.05, 0.4);
  const impliedBuyVolume = (floorReserve + ethReturned) / (floorShareBPS / 10_000);
  const lifetimeVolume = impliedBuyVolume * rng.float(1.6, 2.4);
  const volFactor =
    spec.phase === 'launching'
      ? rng.float(2.5, 9)
      : spec.phase === 'graduating'
        ? rng.float(0.5, 2)
        : graduated
          ? rng.float(0.05, 0.4)
          : spec.phase === 'halted'
            ? rng.float(0.01, 0.1)
            : rng.float(0.1, 1.2);
  const volume24hEth = Math.min(lifetimeVolume, spec.raised * volFactor);
  const avgTradeSize = clamp(spec.raised * 0.02, 0.002, 3);
  const trades24h = Math.max(1, Math.round(volume24hEth / avgTradeSize));
  const floorLifts = Math.max(1, Math.round(impliedBuyVolume / avgTradeSize));
  const buybacks = Math.min(
    420,
    Math.floor((impliedBuyVolume * buybackFeeBPS) / 10_000 / Math.max(1e-6, buybackThreshold)),
  );
  // Only the non-floor leg of a buyback is actually burned.
  const ethBurnedEth = buybacks * buybackThreshold * (1 - floorShareBPS / 10_000);

  // ── changes ───────────────────────────────────────────────────────────────
  const change1h = spec.change24h / rng.float(5, 14) + rng.gauss(0, 0.008);
  const change7d =
    now - createdAt < 7 * DAY
      ? spec.change24h
      : clamp(spec.change24h * rng.float(0.7, 2.3) + rng.gauss(0, 0.05), -0.93, 60);

  const discovery = graduated ? vToken : 0;
  const totalSupply = redeemableSupply + discovery;

  const links: TokenLinks = {
    website: rng.bool(0.55) ? `https://${spec.symbol.toLowerCase()}.${rng.pick(LINK_ROOTS)}` : undefined,
    x: rng.bool(0.8) ? `https://x.com/${spec.symbol.toLowerCase()}` : undefined,
    telegram: rng.bool(0.45) ? `https://t.me/${spec.symbol.toLowerCase()}` : undefined,
    farcaster: rng.bool(0.3) ? `https://warpcast.com/${spec.symbol.toLowerCase()}` : undefined,
  };

  const traders = buildTraders(rng, clamp(Math.round(spec.holders / 8), 14, 48));
  const creatorTrader = traders[0]!;

  const token: DemoToken = {
    address,
    name: spec.name,
    symbol: spec.symbol,
    description: spec.description,
    // Golden-angle hue spacing gives 28 maximally-separated marks; a hash of the
    // address clusters them and the grid ends up looking monochrome.
    hue: Math.round((index * 137.508 + rng.float(-9, 9) + 360) % 360),
    glyph: spec.glyph,
    tags: spec.tags,
    links,

    creator: creatorTrader.address,
    creatorHandle: creatorTrader.handle ?? `${spec.symbol.toLowerCase()}_dev`,
    createdAt,

    phase: spec.phase,
    graduated,
    tradingStopped,

    totalSupply: wei(totalSupply),
    tokenReserve: wei(vToken),
    quoteReserve: wei(vQuote),
    virtualTokenReserve: wei(vToken0),
    virtualQuoteReserve: wei(vQuote0),
    floorReserve: wei(floorReserve),
    treasuryReserve: wei(treasuryReserve),
    snipeSurplus: wei(snipeSurplus),
    redeemableSupply: wei(redeemableSupply),
    graduationQuoteThreshold: wei(threshold),
    buybackThreshold: wei(buybackThreshold),

    feeBPS,
    floorShareBPS,
    buybackFeeBPS,
    antiSnipeStartBPS,
    antiSnipeWindow,
    effectiveFeeBPS: feeBPS,

    spotPrice: wei(spot),
    floorPrice: wei(floorPrice),

    marketCapEth: spot * totalSupply,
    liquidityEth: spec.raised,
    raisedEth: spec.raised,
    floorLockedEth: floorReserve,
    floorRatio: spec.floorRatio,
    graduationProgress: Math.min(1, spec.progress),
    volume24hEth,
    change1h,
    change24h: spec.change24h,
    change7d,
    holderCount: spec.holders,
    trades24h,
    floorLifts,
    buybacks,
    ethBurnedEth,
    safetyScore: 0,
  };

  // `effectiveFeeBPS` and `graduationProgress` are re-read through the curve so
  // the token can never disagree with the maths the trade panel will run.
  const state = curveStateFromToken(token);
  token.effectiveFeeBPS = Math.round(effectiveFeeBPS(state, now));
  token.graduationProgress = graduated ? 1 : graduationProgress(state);

  const trades = buildTrades(token, state, traders, rng.fork('trades'), now, avgTradeSize);
  const holders = buildHolders(token, traders, rng.fork('holders'), now, discovery, totalSupply);
  token.safetyScore = safetyScore(token, holders, now);

  return { token, trades, holders, lifetimeVolume, ethReturned, traders };
}

/* ────────────────────────────────────────────────────────────────────────────
   Trades
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * The generated tape is a SAMPLE of the token's history, not the whole thing —
 * an indexer would page it too. Sizes are drawn around the same `avgTradeSize`
 * that the aggregate `trades24h` is computed from, so the tape and the header
 * numbers imply each other.
 */
function buildTrades(
  token: DemoToken,
  state: CurveState,
  traders: Trader[],
  rng: Rng,
  now: number,
  avgTradeSize: number,
): DemoTrade[] {
  const count = clamp(Math.round(60 + Math.log10(1 + token.raisedEth * 40) * 95 + rng.gauss(0, 30)), 60, 400);
  const anchors = priceAnchors(token, now);
  const lifetime = Math.max(MINUTE, now - token.createdAt);
  const floorNow = weiToNumber(token.floorPrice);
  const antiSnipeEnd = token.createdAt + token.antiSnipeWindow * BLOCK_MS;

  // Clustering: a launch burst, two hype spikes, and a uniform background.
  const spikes = [rng.float(0.25, 0.55), rng.float(0.6, 0.93)];
  const stamps: number[] = [];
  for (let i = 0; i < count; i++) {
    const roll = rng.rand();
    let frac: number;
    if (roll < 0.3) frac = Math.pow(rng.rand(), 2.4) * 0.08;
    else if (roll < 0.5) {
      const s = spikes[rng.int(0, 1)]!;
      frac = clamp(s + rng.gauss(0, 0.015), 0, 1);
    } else frac = rng.rand();
    stamps.push(token.createdAt + frac * lifetime);
  }
  // The tape must feel current: force the newest few into the last hour.
  for (let i = 0; i < Math.min(6, count); i++) stamps[i] = now - rng.float(4_000, HOUR);
  stamps.sort((a, b) => a - b);

  const bearish = token.change24h < 0;
  // A graduated or halted token has no curve left: after the stop, the only
  // thing that can still happen on-chain is `redeemAtFloor`.
  const stoppedAt = token.tradingStopped ? token.createdAt + lifetime * 0.86 : Infinity;
  const sideWeights: Record<TradeSide, number> = {
    buy: bearish ? 42 : 62,
    sell: bearish ? 48 : 30,
    // Redeems cluster on the tokens people actually need the floor for.
    redeem: token.tradingStopped ? 14 : bearish ? 6 : 2,
    buyback: token.buybacks > 0 ? 3 : 0,
  };
  const sides: TradeSide[] = ['buy', 'sell', 'redeem', 'buyback'];
  const weights = sides.map((s) => sideWeights[s]);

  return stamps
    .map((ts, i) => {
      const trader = traders[rng.int(0, traders.length - 1)]!;
      let side = rng.pickWeighted(sides, weights);
      // Before the curve closed there was nothing to redeem against yet, and a
      // graduated/halted token has no curve left to buy or sell on.
      if (ts > stoppedAt) side = 'redeem';
      if (ts < token.createdAt + 2 * MINUTE && side !== 'buy') side = 'buy';

      const walkPrice = anchorPriceAt(anchors, ts) * Math.exp(rng.gauss(0, 0.012));
      const price = side === 'redeem' ? Math.min(floorNow, walkPrice) || floorNow : walkPrice;
      const ethAmount = Math.max(
        1e-6,
        avgTradeSize * Math.exp(rng.gauss(0, 1.15)) * (ts < token.createdAt + 0.08 * lifetime ? 1.8 : 1),
      );
      const tokenAmount = price > 0 ? ethAmount / price : 0;
      const antiSnipe = side === 'buy' && ts < antiSnipeEnd;

      const trade: DemoTrade = {
        id: `${token.symbol}-${i}`,
        tokenAddress: token.address,
        side,
        trader: trader.address,
        traderHandle: trader.handle,
        ethAmount,
        tokenAmount,
        price,
        ts: Math.round(ts),
        txHash: rng.txHash(),
      };
      if (side === 'buy') {
        // Every buy routes `floorShareBPS` of gross into the floor reserve, so
        // every buy is a floor lift.
        trade.floorLiftEth = (ethAmount * state.floorShareBPS) / 10_000;
        if (antiSnipe) trade.antiSnipe = true;
      }
      if (side === 'buyback') trade.floorLiftEth = (ethAmount * state.floorShareBPS) / 10_000;
      return trade;
    })
    .reverse(); // newest first, which is how every list in the UI reads
}

/* ────────────────────────────────────────────────────────────────────────────
   Holders
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Power-law distribution over `totalSupply`. Shares are scaled to a "tracked"
 * fraction below 1 so the long tail of dust wallets the demo does not enumerate
 * is accounted for — and so the sum can never exceed 1.
 */
function buildHolders(
  token: DemoToken,
  traders: Trader[],
  rng: Rng,
  now: number,
  discovery: number,
  totalSupply: number,
): DemoHolder[] {
  // The migrated DEX position occupies one of the 40 rows, so leave room for it.
  const hasDiscovery = discovery > 0 && totalSupply > 0 && discovery / totalSupply > 0.001;
  const count = clamp(
    Math.round(12 + Math.log10(1 + token.holderCount) * 9 + rng.gauss(0, 3)),
    12,
    hasDiscovery ? 39 : 40,
  );
  const spot = weiToNumber(token.spotPrice);
  const floorNow = weiToNumber(token.floorPrice);
  const anchors = priceAnchors(token, now);
  const lifetime = Math.max(MINUTE, now - token.createdAt);

  // The migrated DEX position holds the discovery supply, which was never
  // floor-backed — it is the biggest single line on a graduated token.
  const discoveryShare = totalSupply > 0 ? discovery / totalSupply : 0;
  const trackable = clamp(rng.float(0.58, 0.9) * (1 - discoveryShare), 0.05, 0.97);

  const alpha = rng.float(0.95, 1.35);
  const raw = Array.from({ length: count }, (_, i) => 1 / Math.pow(i + 0.7, alpha));
  const rawSum = raw.reduce((a, b) => a + b, 0);

  const creatorRank = rng.int(0, 3);
  const hasVault = rng.bool(0.4);
  const vaultRank = hasVault ? rng.int(1, Math.min(5, count - 1)) : -1;
  const treasuryRank = token.buybacks > 0 ? rng.int(2, Math.min(8, count - 1)) : -1;

  const holders: DemoHolder[] = [];

  if (hasDiscovery) {
    const balance = discovery;
    holders.push({
      rank: 0,
      address: rng.hexAddress(),
      handle: 'dex-pool',
      balance: wei(balance),
      share: discoveryShare,
      valueEth: balance * spot,
      floorValueEth: 0, // discovery supply was never floor-backed
      pnlEth: 0,
      label: 'protocol',
      firstBuyAt: token.createdAt + lifetime * 0.98,
    });
  }

  for (let i = 0; i < count; i++) {
    const share = (raw[i]! / rawSum) * trackable;
    const balance = share * totalSupply;
    // Big holders got in early; the tail arrived late.
    const firstBuyAt = token.createdAt + lifetime * clamp(Math.pow(rng.rand(), 2 - i / count) * (0.15 + i / count), 0, 0.99);
    const avgEntry = anchorPriceAt(anchors, firstBuyAt) * rng.float(0.82, 1.2);
    const trader = traders[i % traders.length]!;

    let label: HolderLabel | undefined;
    if (i === creatorRank) label = 'creator';
    else if (i === vaultRank) label = 'vault';
    else if (i === treasuryRank) label = 'treasury';
    else if (share > 0.035 && i < 6) label = 'whale';

    holders.push({
      rank: 0,
      address: i === creatorRank ? token.creator : trader.address,
      handle: i === creatorRank ? token.creatorHandle : trader.handle,
      balance: wei(balance),
      share,
      valueEth: balance * spot,
      floorValueEth: balance * floorNow,
      pnlEth: balance * (spot - avgEntry),
      label,
      firstBuyAt: Math.round(firstBuyAt),
    });
  }

  holders.sort((a, b) => b.share - a.share);
  holders.forEach((h, i) => {
    h.rank = i + 1;
  });
  return holders;
}

/* ────────────────────────────────────────────────────────────────────────────
   Safety score
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Composite 0–100 shown on the safety meter. Weights, and why:
 *
 *   34%  floor coverage      — the protocol's actual guarantee; a 0.6 floor ratio
 *                              or better is treated as full marks.
 *   20%  holder distribution — 1 - top-10 concentration, rescaled. Concentration
 *                              is the failure mode a floor cannot protect against.
 *   18%  graduation progress — a curve near its threshold is closer to permanent
 *                              liquidity and a locked floor.
 *   14%  age                 — 30 days of survival is full marks; brand-new
 *                              tokens are structurally riskier regardless of ratio.
 *    8%  buyback activity    — evidence the fee machinery is actually running.
 *    6%  raise depth         — thin curves are easy to move.
 *
 * A halted token is docked 25 points: redeem-at-floor still works, but the market
 * does not, and the meter must not read "safe" on a stopped token.
 */
function safetyScore(token: DemoToken, holders: DemoHolder[], now: number): number {
  const top10 = holders
    .filter((h) => h.label !== 'protocol')
    .slice(0, 10)
    .reduce((a, h) => a + h.share, 0);
  const ageDays = (now - token.createdAt) / DAY;

  const score =
    34 * clamp(token.floorRatio / 0.6, 0, 1) +
    20 * clamp((1 - top10) / 0.75, 0, 1) +
    18 * clamp(token.graduationProgress, 0, 1) +
    14 * clamp(ageDays / 30, 0, 1) +
    8 * clamp(token.buybacks / 6, 0, 1) +
    6 * clamp(token.raisedEth / 50, 0, 1);

  // Capped below 100: nothing here is risk-free, and the meter must not imply it.
  return Math.round(clamp(token.tradingStopped && !token.graduated ? score - 25 : score, 1, 98));
}

/* ────────────────────────────────────────────────────────────────────────────
   User persona
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Positions are hand-picked so the portfolio page has a story: two clear winners,
 * a flat one, and two losers where the floor is doing visible work.
 *
 * NOTE on "underwater but protected": a position can never be fully covered by
 * the floor while it is underwater, because `floorPrice <= spot` by construction —
 * if the floor exceeded spot, buying and redeeming would be free money. What the
 * floor actually gives an underwater holder is a HARD BOUND on further loss, so
 * these positions are built to make `floorValueEth` a large fraction of `costEth`.
 */
function buildUser(bundles: TokenBundle[], now: number): DemoUser {
  const rng = makeRng('bull.markets/user');
  const address = rng.hexAddress();
  const handle = 'orbitalslop';
  const bySymbol = new Map(bundles.map((b) => [b.token.symbol, b] as const));

  const plan: { symbol: string; supplyShare: number; entryVsSpot: number }[] = [
    { symbol: 'RATCH', supplyShare: 0.0021, entryVsSpot: 0.41 }, // early, deep in profit
    { symbol: 'HAMSTR', supplyShare: 0.0064, entryVsSpot: 0.52 }, // rode the +166%
    { symbol: 'CLIP', supplyShare: 0.0009, entryVsSpot: 0.74 }, // modest winner
    { symbol: 'DEEPFL', supplyShare: 0.0038, entryVsSpot: 1.09 }, // slightly underwater, 0.78 floor
    { symbol: 'EXIT', supplyShare: 0.0121, entryVsSpot: 1.12 }, // underwater, floor covering ~61% of cost
    { symbol: 'DUST', supplyShare: 0.019, entryVsSpot: 1.44 }, // small and sad
  ];

  const positions: DemoPosition[] = [];
  for (const p of plan) {
    const bundle = bySymbol.get(p.symbol);
    if (!bundle) continue;
    const token = bundle.token;
    const spot = weiToNumber(token.spotPrice);
    const floorNow = weiToNumber(token.floorPrice);
    const balance = weiToNumber(token.totalSupply) * p.supplyShare;
    const avgEntry = spot * p.entryVsSpot;
    const costEth = balance * avgEntry;
    const valueEth = balance * spot;
    positions.push({
      token,
      balance: wei(balance),
      avgEntry,
      costEth,
      valueEth,
      floorValueEth: balance * floorNow,
      pnlEth: valueEth - costEth,
      pnlPct: costEth > 0 ? (valueEth - costEth) / costEth : 0,
    });
  }

  // Two of the world's tokens are re-attributed to the user so /portfolio has
  // real launches to show. Re-attribution keeps every other number intact.
  const launches = ['TUNG', 'NEON']
    .map((s) => bySymbol.get(s))
    .filter((b): b is TokenBundle => Boolean(b))
    .map((b) => {
      b.token.creator = address;
      b.token.creatorHandle = handle;
      return b.token;
    });

  const activity: DemoActivity[] = [];
  const pool = [...positions.map((p) => p.token), ...launches];
  for (let i = 0; i < 25; i++) {
    const token = pool[rng.int(0, pool.length - 1)]!;
    const isLaunch = i > 21 && launches.length > 0;
    const kind: ActivityKind = isLaunch
      ? 'launch'
      : rng.pickWeighted<ActivityKind>(
          ['buy', 'sell', 'redeem', 'floor-lift', 'claim', 'graduate'],
          [40, 24, 10, 14, 8, 4],
        );
    const ethAmount = Math.max(0.0004, Math.abs(rng.gauss(0.28, 0.4)));
    const price = weiToNumber(token.spotPrice) || 1e-9;
    activity.push({
      id: `user-act-${i}`,
      kind,
      token: {
        address: token.address,
        name: token.name,
        symbol: token.symbol,
        hue: token.hue,
        glyph: token.glyph,
      },
      actor: address,
      actorHandle: handle,
      ethAmount: kind === 'launch' ? undefined : ethAmount,
      tokenAmount: kind === 'launch' ? undefined : ethAmount / price,
      ts: Math.round(now - rng.float(2 * MINUTE, 26 * DAY)),
      txHash: rng.txHash(),
      note: kind === 'claim' ? 'Creator fee claim' : undefined,
    });
  }
  activity.sort((a, b) => b.ts - a.ts);

  const feesEarnedEth = launches.reduce(
    (acc, t) => acc + (t.volume24hEth * 9 * (t.feeBPS - t.buybackFeeBPS)) / 10_000,
    0,
  );

  return {
    address,
    handle,
    ens: `${handle}.eth`,
    balanceEth: rng.float(1.8, 6.4),
    joinedAt: Math.round(now - rng.float(140 * DAY, 400 * DAY)),
    positions,
    launches,
    activity,
    realizedPnlEth: rng.float(-1.4, 8.2),
    feesEarnedEth,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Feed
   ──────────────────────────────────────────────────────────────────────────── */

const FEED_KINDS: ActivityKind[] = ['buy', 'sell', 'redeem', 'launch', 'graduate', 'floor-lift', 'buyback', 'claim'];

/** Cross-token firehose, newest first, all inside the last ~6 hours. */
export function buildFeed(bundles: TokenBundle[], now: number, count = 60): DemoActivity[] {
  const rng = makeRng('bull.markets/feed');
  const weights = bundles.map((b) => Math.max(0.01, b.token.volume24hEth));
  const out: DemoActivity[] = [];

  for (let i = 0; i < count; i++) {
    const bundle = rng.pickWeighted(bundles, weights);
    const token = bundle.token;
    const trader = bundle.traders[rng.int(0, bundle.traders.length - 1)]!;
    const kind = pickFeedKind(token, rng);
    const price = weiToNumber(token.spotPrice) || 1e-9;
    const ethAmount = Math.max(0.0002, Math.abs(rng.gauss(0, 1)) * clamp(token.raisedEth * 0.02, 0.002, 4));
    out.push({
      id: `feed-${i}`,
      kind,
      token: {
        address: token.address,
        name: token.name,
        symbol: token.symbol,
        hue: token.hue,
        glyph: token.glyph,
      },
      actor: trader.address,
      actorHandle: trader.handle,
      ethAmount: kind === 'launch' ? undefined : ethAmount,
      tokenAmount: kind === 'launch' || kind === 'claim' ? undefined : ethAmount / price,
      ts: Math.round(now - rng.float(3_000, 6 * HOUR)),
      txHash: rng.txHash(),
      note: noteFor(kind, token.symbol),
    });
  }

  out.sort((a, b) => b.ts - a.ts);
  return out;
}

function pickFeedKind(token: DemoToken, rng: Rng): ActivityKind {
  const w: Record<ActivityKind, number> = {
    buy: token.tradingStopped ? 0 : token.change24h >= 0 ? 46 : 30,
    sell: token.tradingStopped ? 0 : 24,
    redeem: token.tradingStopped ? 40 : token.change24h < 0 ? 8 : 3,
    'floor-lift': token.tradingStopped ? 0 : 12,
    buyback: token.buybacks > 0 && !token.tradingStopped ? 5 : 0,
    launch: token.phase === 'launching' ? 8 : 0,
    graduate: token.phase === 'graduating' ? 3 : 0,
    claim: 4,
  };
  return rng.pickWeighted(FEED_KINDS, FEED_KINDS.map((kind) => w[kind]));
}

function noteFor(kind: ActivityKind, symbol: string): string | undefined {
  switch (kind) {
    case 'floor-lift':
      return 'Floor ratcheted up';
    case 'redeem':
      return 'Redeemed at floor';
    case 'buyback':
      return 'Buyback and burn';
    case 'graduate':
      return `${symbol} graduated`;
    case 'launch':
      return `${symbol} launched`;
    default:
      return undefined;
  }
}

/* ────────────────────────────────────────────────────────────────────────────
   Assembly
   ──────────────────────────────────────────────────────────────────────────── */

export interface GeneratedWorld extends DemoWorld {
  /** Per-token tapes and holder lists, keyed by lowercased address. */
  trades: Map<string, DemoTrade[]>;
  holders: Map<string, DemoHolder[]>;
  /** Float curve state per token, so trades can be applied without re-deriving. */
  curves: Map<string, CurveState>;
  traders: Map<string, Trader[]>;
}

export function generateWorld(seed = 'bull.markets/v1'): GeneratedWorld {
  const now = Date.now();
  const rng = makeRng(seed);
  const bundles = SPECS.map((spec, i) => buildToken(spec, i, now));

  const user = buildUser(bundles, now);
  const feed = buildFeed(bundles, now);

  // Every aggregate below is a real sum or derivation over `bundles`; the hero
  // stats must not be able to contradict the list underneath them.
  const tokens = bundles.map((b) => b.token);
  const sumHolders = tokens.reduce((a, t) => a + t.holderCount, 0);
  const stats: ProtocolStats = {
    tokensLaunched: tokens.length,
    volumeAllTimeEth: bundles.reduce((a, b) => a + b.lifetimeVolume, 0),
    volume24hEth: tokens.reduce((a, t) => a + t.volume24hEth, 0),
    floorLockedEth: tokens.reduce((a, t) => a + t.floorLockedEth, 0),
    ethReturnedEth: bundles.reduce((a, b) => a + b.ethReturned, 0),
    graduatedCount: tokens.filter((t) => t.graduated).length,
    // Wallets hold several tokens each, so the union is well below the sum.
    tradersCount: Math.round(sumHolders * 0.72),
    ethBurnedEth: tokens.reduce((a, t) => a + t.ethBurnedEth, 0),
    volume24hChange: rng.float(-0.25, 0.62),
    floorLockedChange: rng.float(0.01, 0.14),
  };

  const world: GeneratedWorld = {
    tokens,
    stats,
    user,
    feed,
    floorLockedSeries: buildFloorLockedSeries(stats),
    trades: new Map(bundles.map((b) => [b.token.address.toLowerCase(), b.trades])),
    holders: new Map(bundles.map((b) => [b.token.address.toLowerCase(), b.holders])),
    curves: new Map(bundles.map((b) => [b.token.address.toLowerCase(), curveStateFromToken(b.token)])),
    traders: new Map(bundles.map((b) => [b.token.address.toLowerCase(), b.traders])),
  };
  return world;
}

/* ────────────────────────────────────────────────────────────────────────────
   Writing curve state back onto a token
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Rebuilds the display aggregates on a token from a (possibly advanced) curve
 * state. Returns a NEW token object so React sees the change.
 *
 * `floorPrice` is taken through `curve.floorPrice`, which is a `Math.max` against
 * the stored value — so no code path here, including live jitter, can lower it.
 */
export function applyCurveState(token: DemoToken, state: CurveState, now = Date.now()): DemoToken {
  const spot = spotPrice(state);
  const floor = floorPriceOf(state);
  const discovery = state.graduated ? state.vToken : 0;
  const totalSupply = state.redeemableSupply + discovery;
  const raisedEth = Math.max(0, state.vQuote - state.vQuote0);

  return {
    ...token,
    graduated: state.graduated,
    tradingStopped: state.tradingStopped,
    phase: phaseFor(token, state, now),

    totalSupply: wei(totalSupply),
    tokenReserve: wei(state.vToken),
    quoteReserve: wei(state.vQuote),
    floorReserve: wei(state.floorReserve),
    treasuryReserve: wei(state.treasuryReserve),
    snipeSurplus: wei(state.snipeSurplus),
    redeemableSupply: wei(state.redeemableSupply),

    effectiveFeeBPS: Math.round(effectiveFeeBPS(state, now)),
    spotPrice: wei(spot),
    floorPrice: wei(floor),

    marketCapEth: spot * totalSupply,
    liquidityEth: raisedEth,
    raisedEth,
    floorLockedEth: state.floorReserve,
    floorRatio: spot > 0 ? clamp(floor / spot, 0, 1) : 0,
    graduationProgress: state.graduated ? 1 : graduationProgress(state),
  };
}

/** Phase is derived, never stored: it is a read of the curve, not an attribute. */
function phaseFor(token: DemoToken, state: CurveState, now: number): TokenPhase {
  if (state.graduated) return 'graduated';
  if (state.tradingStopped) return 'halted';
  if (now < state.createdAt + state.antiSnipeWindow * BLOCK_MS) return 'launching';
  // "Within 15% of the threshold" — the same rule the type comment states.
  if (graduationProgress(state) >= 0.85) return 'graduating';
  return token.phase === 'halted' ? 'halted' : 'trading';
}

/* ────────────────────────────────────────────────────────────────────────────
   Launching a new token at runtime
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Mints a brand-new token from `/launch` input. The dev buy is applied through the
 * real curve maths, so the resulting card shows a genuine first floor lift and a
 * genuine anti-snipe fee rather than decorative zeros.
 */
export function makeLaunchedToken(input: LaunchInput, creator: DemoUser, index: number, now = Date.now()): DemoToken {
  const rng = makeRng(`launch:${input.symbol}:${now}`);
  const address = rng.hexAddress();
  const threshold = Math.max(0.5, input.graduationQuoteThresholdEth);
  // A fresh curve starts at 30% of its threshold in virtual reserve, the same
  // geometry the generated world's mid-progress tokens use.
  const vQuote0 = threshold * 0.3;

  const state: CurveState = {
    vQuote: vQuote0,
    vToken: SUPPLY_CAP,
    vQuote0,
    vToken0: SUPPLY_CAP,
    floorReserve: 0,
    snipeSurplus: 0,
    treasuryReserve: 0,
    redeemableSupply: 0,
    floorPrice: 0,
    feeBPS: input.feeBPS,
    floorShareBPS: input.floorShareBPS,
    buybackFeeBPS: input.buybackFeeBPS,
    antiSnipeStartBPS: input.antiSnipeStartBPS,
    antiSnipeWindow: input.antiSnipeWindow,
    createdAt: now,
    graduationQuoteThreshold: threshold,
    graduated: false,
    tradingStopped: false,
  };

  const shell: DemoToken = {
    address,
    name: input.name,
    symbol: input.symbol,
    description: input.description,
    hue:
      typeof input.hue === 'number' && Number.isFinite(input.hue)
        ? Math.round(((input.hue % 360) + 360) % 360)
        : Math.round((index * 137.508 + 360) % 360),
    glyph: input.glyph || '🚀',
    tags: input.tags.length > 0 ? input.tags : ['meme'],
    links: input.links ?? {},
    creator: creator.address,
    creatorHandle: creator.handle,
    createdAt: now,
    phase: 'launching',
    graduated: false,
    tradingStopped: false,
    totalSupply: 0n,
    tokenReserve: wei(SUPPLY_CAP),
    quoteReserve: wei(vQuote0),
    virtualTokenReserve: wei(SUPPLY_CAP),
    virtualQuoteReserve: wei(vQuote0),
    floorReserve: 0n,
    treasuryReserve: 0n,
    snipeSurplus: 0n,
    redeemableSupply: 0n,
    graduationQuoteThreshold: wei(threshold),
    buybackThreshold: wei(Math.max(0.01, threshold * 0.005)),
    feeBPS: input.feeBPS,
    floorShareBPS: input.floorShareBPS,
    buybackFeeBPS: input.buybackFeeBPS,
    antiSnipeStartBPS: input.antiSnipeStartBPS,
    antiSnipeWindow: input.antiSnipeWindow,
    effectiveFeeBPS: input.feeBPS + input.antiSnipeStartBPS,
    spotPrice: wei(vQuote0 / SUPPLY_CAP),
    floorPrice: 0n,
    marketCapEth: 0,
    liquidityEth: 0,
    raisedEth: 0,
    floorLockedEth: 0,
    floorRatio: 0,
    graduationProgress: 0,
    volume24hEth: 0,
    change1h: 0,
    change24h: 0,
    change7d: 0,
    holderCount: 0,
    trades24h: 0,
    floorLifts: 0,
    buybacks: 0,
    ethBurnedEth: 0,
    safetyScore: 0,
  };

  // Run the dev buy through the real curve so the new card shows a genuine first
  // floor lift and the full anti-snipe surcharge, not decorative zeros.
  const seeded = input.devBuyEth > 0 ? buyExactIn(state, input.devBuyEth, now).next : state;
  return applyCurveState(shell, seeded, now);
}

export { SUPPLY_CAP };
export type { TokenBundle, Trader };
